import {
  IMeasurement,
  IMeasurementTypeValidation,
} from "../../../@types/admin/AdminMeasurement";
import { useArchiveMeasurement } from "../../../hooks/mutations/admin/measurements";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  FilterButton,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalLabel,
  ModalTitle,
  ModalHeader,
  ModalSubtitle,
  ModalValue,
  WarningNotice,
} from "../shared";
import { ValueList, ValueRow } from "./AdminPatients.styles";
import { summariseMeasurementValues } from "./measurementEdit";

interface IArchiveMeasurementModalProps {
  measurement: IMeasurement;
  measurementTypes: IMeasurementTypeValidation[];
  onClose: () => void;
}

/**
 * Archive/unarchive confirmation — Retool's `archiveMeasurementModal`
 * (EN-5158). The endpoint is a toggle (`archive: !isArchived`), so the same
 * dialog covers both directions.
 *
 * It lists the values being archived, as Retool did: archiving hides a
 * measurement from the clinical record, and "measurement 84213" alone is not
 * enough to confirm it is the right one.
 */
export function ArchiveMeasurementModal({
  measurement,
  measurementTypes,
  onClose,
}: IArchiveMeasurementModalProps) {
  const { showToast } = useToast();
  const { archiveMeasurement, isArchivingMeasurement } =
    useArchiveMeasurement();

  const isArchiving = !measurement.isArchived;
  const action = isArchiving ? "Archive" : "Unarchive";
  const values = summariseMeasurementValues(measurement, measurementTypes);

  const handleConfirm = async () => {
    try {
      await archiveMeasurement({
        cmsPatientId: measurement.patientId,
        measurementId: measurement.id,
        archive: isArchiving,
      });
      showToast(
        `Success ${isArchiving ? "archiving" : "unarchiving"} measurement`,
        "success"
      );
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          `There was an error ${
            isArchiving ? "archiving" : "unarchiving"
          } measurement`
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>{action} measurement</ModalTitle>
        <ModalSubtitle>
          Measurement {measurement.id} · {measurement.patientName} · taken{" "}
          {getLongFormattedDate(measurement.time, "at an unknown time")}
        </ModalSubtitle>
      </ModalHeader>

      <ModalForm>
        {values.length > 0 ? (
          <ValueList>
            {values.map((value) => (
              <ValueRow key={value.label}>
                <ModalLabel>{value.label}</ModalLabel>
                <ModalValue>{value.value}</ModalValue>
              </ValueRow>
            ))}
          </ValueList>
        ) : (
          <ModalValue>This measurement has no recorded values.</ModalValue>
        )}

        {isArchiving && (
          <WarningNotice>
            Archiving hides this measurement from the patient&apos;s clinical
            record. It can be unarchived from this page.
          </WarningNotice>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isArchivingMeasurement}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isArchivingMeasurement}
            onClick={handleConfirm}
          >
            {isArchivingMeasurement ? `${action}...` : action}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
